import {
  Banner,
  Box,
  Button,
  ConfirmCancelDialog,
  Text,
  Tooltip,
  useDialogState,
  useToast,
} from '@color/continuum';
import { type ReactNode, useMemo } from 'react';

import { useMarkConfirmationsAsPendingMutation } from './api';
import {
  APPROVE_PRIMER_BLOCKER_MESSAGE,
  isApproveBlockedByPrimers,
} from './DetailView/PrimersSection/lifecycle';
import { SelectedConfirmationsTable } from './SelectedConfirmationsTable';
import { VariantConfirmation, VariantConfirmationState } from './types';

export function isVariantConfirmationStateApprovableForSendout(
  state: VariantConfirmationState
): boolean {
  return state === VariantConfirmationState.QUEUED;
}

export interface ApproveForSendoutDialogProps {
  confirmations: VariantConfirmation[];
  onClose: () => void;
  onSuccess: () => void;
  dialogVariant: 'batch' | 'single';
}

export const ApproveForSendoutDialog = ({
  confirmations,
  onClose,
  onSuccess,
  dialogVariant,
}: ApproveForSendoutDialogProps) => {
  const { showToast } = useToast();
  const { mutateAsync: markAsPending, isPending } = useMarkConfirmationsAsPendingMutation();

  // Mirror `mark_as_pending`'s primer gate so the button is visibly disabled
  // with an explanation, not silently rejected by the backend.
  const blockedConfirmations = useMemo(
    () => confirmations.filter(isApproveBlockedByPrimers),
    [confirmations]
  );
  const hasPrimerBlockers = blockedConfirmations.length > 0;

  const handleSubmit = async () => {
    try {
      await markAsPending({ ids: confirmations.map((c) => c.id) });
      showToast(
        confirmations.length === 1
          ? 'Confirmation approved for sendout.'
          : 'Confirmations approved for sendout.',
        { severity: 'success' }
      );
      onSuccess();
    } catch {
      // mutateAsync rethrows; the mutation hook already shows an error toast.
    }
  };

  const isSingleLayout = dialogVariant === 'single';
  const title = isSingleLayout
    ? 'Approve for sendout'
    : `Approve ${confirmations.length} confirmation${confirmations.length !== 1 ? 's' : ''} for sendout`;

  const submitDisabled = isPending || hasPrimerBlockers;
  const submitTooltip = hasPrimerBlockers ? APPROVE_PRIMER_BLOCKER_MESSAGE : '';

  return (
    <ConfirmCancelDialog
      title={title}
      confirmButtonText="Approve"
      confirmButtonProps={{ disabled: submitDisabled, title: submitTooltip }}
      onAccept={handleSubmit}
      onCancel={onClose}
      dialogIsOpen
      fullWidth
      maxWidth={isSingleLayout ? 'sm' : 'md'}
    >
      {hasPrimerBlockers && (
        <Banner severity="warning" variant="in-component" sx={{ marginBottom: 2 }}>
          {isSingleLayout
            ? APPROVE_PRIMER_BLOCKER_MESSAGE
            : `${APPROVE_PRIMER_BLOCKER_MESSAGE} Affected: ${blockedConfirmations
                .map((c) => c.accession_number)
                .join(', ')}.`}
        </Banner>
      )}
      {isSingleLayout ? (
        <Text>This will approve the confirmation for vendor sendout. Continue?</Text>
      ) : (
        <SelectedConfirmationsTable confirmations={confirmations} />
      )}
    </ConfirmCancelDialog>
  );
};

export function useVariantConfirmationApproveForSendoutAction(
  confirmation: VariantConfirmation,
  options: { onSuccess: () => void }
): { button: ReactNode | null } {
  const { dialogIsOpen, openDialog, closeDialog } = useDialogState();

  if (!isVariantConfirmationStateApprovableForSendout(confirmation.state)) {
    return { button: null };
  }

  const blockedByPrimers = isApproveBlockedByPrimers(confirmation);

  const button = (
    <>
      <Tooltip title={blockedByPrimers ? APPROVE_PRIMER_BLOCKER_MESSAGE : ''}>
        <Box component="span">
          <Button variant="contained" size="small" disabled={blockedByPrimers} onClick={openDialog}>
            Approve for sendout
          </Button>
        </Box>
      </Tooltip>
      {dialogIsOpen && (
        <ApproveForSendoutDialog
          confirmations={[confirmation]}
          dialogVariant="single"
          onClose={closeDialog}
          onSuccess={() => {
            closeDialog();
            options.onSuccess();
          }}
        />
      )}
    </>
  );

  return { button };
}
